import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useMangoApi } from '~/atoms/MangoApi'
import Logger from '~/services/Logger'
import { getUserFromToken } from '~/utils/auth'

import {
  ApiError,
  SessionUser,
  User,
  UserRoles,
  UsersQueryParams,
} from '@mangovoice/types'
import { ExtensionInstance, Pbx } from '@mangovoice/types'

interface UserSessionDependencyData {
  pbx: Pbx
  userExtensions: ExtensionInstance[]
  pbxUsers: User[]
  userRoles?: UserRoles
  sessionUser?: SessionUser
}
interface UseGetUserSessionDependenciesParams {
  onQuerySuccess?: (data: UserSessionDependencyData) => void
  onQueryErrored?: (pbxId: number, error: ApiError) => void
  options?: UseQueryOptions<UserSessionDependencyData, ApiError>
}
const logger = new Logger('useGetUserSessionDependencies')
export const useGetUserSessionDependencies = ({
  onQuerySuccess,
  onQueryErrored,
  options,
}: UseGetUserSessionDependenciesParams) => {
  const mangoApi = useMangoApi()
  const [selectedPbx, setSelectedPbx] = useState<Pbx | null>(null)
  const { authenticationState } = useAuthenticationState()

  const query = useQuery<UserSessionDependencyData, ApiError>({
    queryKey: ['userSessionDependencies', selectedPbx?.id ?? 0],
    queryFn: async () => {
      const { id: selectedPbxId } = selectedPbx ?? {}
      if (!selectedPbxId) {
        const error = { message: 'No pbx selected' }
        onQueryErrored?.(0, error)
        return Promise.reject(error)
      }

      //fetch user extension data
      const {
        ok: userExtensionsOk,
        data: userExtensionsResult,
        status: userExtensionsStatus,
      } = await mangoApi.getUserExtensions(selectedPbx?.id)
      logger.info('User Extensions Result', {
        userExtensionsOk,
        userExtensionsResult,
        userExtensionsStatus,
      })

      if (!userExtensionsOk || !userExtensionsResult) {
        const error: ApiError = {
          ...userExtensionsResult,
          status: userExtensionsStatus,
        }
        onQueryErrored?.(selectedPbxId, error)
        return Promise.reject(error)
      }

      const useQueryParams = {
        pbx_ids: [selectedPbxId],
        limit: 999,
      } as UsersQueryParams

      const {
        ok: pbxUsersOk,
        data: pbxUsersResult,
        status: pbxUsersStatus,
      } = await mangoApi.getPbxUsers(useQueryParams)

      if (!pbxUsersOk || !pbxUsersResult) {
        const error: ApiError = {
          ...pbxUsersResult,
          status: pbxUsersStatus,
        }
        onQueryErrored?.(selectedPbxId, error)
        return Promise.reject(error)
      }

      const sessionUser = getUserFromToken(
        authenticationState.appSessionJwts?.access_token,
      )

      let userRoles
      if (sessionUser?.id) {
        const {
          ok: userRolesOk,
          data: userRolesResult,
          status: userRolesStatus,
        } = await mangoApi.getUserRoles(sessionUser.id)

        if (!userRolesOk || !userRolesResult) {
          const error: ApiError = {
            ...userRolesResult,
            status: userRolesStatus,
          }
          onQueryErrored?.(selectedPbxId, error)
          return Promise.reject(error)
        }
        userRoles = userRolesResult
      }

      const result = {
        pbx: selectedPbx!,
        pbxUsers: pbxUsersResult.results,
        userExtensions: userExtensionsResult.results,
        userRoles,
        sessionUser,
      }
      onQuerySuccess?.(result)
      return result
    },
    ...options,
    enabled: false,
    retry: false,
  })

  const refetch = (pbx: Pbx) => {
    setSelectedPbx(pbx)
  }

  useEffect(() => {
    if (!selectedPbx) {
      return
    }

    query.refetch()
  }, [selectedPbx])

  return {
    ...query,
    refetch,
  }
}
